import { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { Box, Button, CardContent, CardHeader, IconButton, Paper, Stack, styled } from "@mui/material";
import { DataGridPro, GridColDef, GridRowOrderChangeParams } from "@mui/x-data-grid-pro";
import { useState } from "react";
import { toast } from "react-toastify";
import type { SaveEmailMatchMutationVariables } from "../../graphql/types";
import { Recipient, Rule, SaveEmailMatchMutation, SaveEmailMatchDocument } from "../../graphql/types";

import { PencilIcon } from "@phosphor-icons/react/dist/csr/Pencil";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { DeactivateRuleWarningModal } from "./DeactivateRuleWarningModal";
import { EmailHeader } from "./EmailIngestHeader";
import { EmailRuleModal } from "./EmailRuleModal";

type RecipientsListProps = {
  email: string;
  rules?: Rule[];
};

const StyledCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  minWidth: "550px",
  marginBottom: theme.spacing(4),
}));

const RecipientCardContent = styled(CardContent)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const columns = (deactivateRule: (ruleId: string) => void, editRule: (ruleId: string) => void): GridColDef[] => [
  {
    field: "sender",
    headerName: "Sender",
    flex: 1,
    renderCell: (params) => <span>{params.row.senderRegex || params.row.senderGlob}</span>,
  },
  {
    field: "subject",
    headerName: "Subject",
    flex: 1,
    renderCell: (params) => <span>{params.row.subjectRegex || params.row.subjectGlob}</span>,
  },
  {
    field: "path",
    headerName: "Destination",
    flex: 2,
  },
  {
    field: "actions",
    headerName: "Actions",
    renderCell: (params) => {
      return (
        <Stack spacing={1} direction="row">
          <IconButton
            onClick={() => editRule(params.row.id)}
            aria-label={`Edit rule with path ${params.row.path}`}
            role="button"
          >
            <PencilIcon />
          </IconButton>
          <IconButton
            onClick={() => deactivateRule(params.row.id)}
            aria-label={`Delete rule with path ${params.row.path}`}
            role="button"
          >
            <TrashIcon />
          </IconButton>
        </Stack>
      );
    },
  },
];

enum ModalToShow {
  None,
  DeactivateRuleWarning,
  DeactivateRecipientModal,
  EditRule,
}

const RecipientCard = ({ email, rules }: RecipientsListProps) => {
  const [modalToShow, setModalToShow] = useState<ModalToShow>(ModalToShow.None);
  const [selectedRule, setSelectedRule] = useState<Rule | undefined>(undefined);

  const updateCache = (cache: ApolloCache, { data }: { data?: SaveEmailMatchMutation | null }) => {
    if (data?.save_emailmatch?.__typename === "SaveEmailMatchOk") {
      const updatedRules = data?.save_emailmatch;

      if (updatedRules) {
        cache.modify({
          fields: {
            recipients(existingRecipients = []) {
              return existingRecipients.map((recipient: Recipient) => {
                if (recipient.email === updatedRules.email) {
                  return {
                    ...recipient,
                    rules: updatedRules.rules,
                  };
                }
                return recipient;
              });
            },
          },
        });
      }
    }
  };

  const [saveEmailMatch] = useMutation<SaveEmailMatchMutation, SaveEmailMatchMutationVariables>(
    SaveEmailMatchDocument,
    {
      onCompleted: (data) => {
        if (data?.save_emailmatch?.__typename === "SaveEmailMatchOk") {
          toast.success("Successfully updated rules for recipient");
        }
      },
      update: updateCache,
    }
  );

  const deactivateRule = (ruleId: string) => {
    setSelectedRule(
      rules?.find((rule) => {
        return rule.id === ruleId;
      })
    );
    if ((rules?.length || 0) > 1) {
      setModalToShow(ModalToShow.DeactivateRuleWarning);
    } else {
      setModalToShow(ModalToShow.DeactivateRecipientModal);
    }
  };

  const editRule = (ruleId: string) => {
    setSelectedRule(
      rules?.find((rule) => {
        return rule.id === ruleId;
      })
    );
    setModalToShow(ModalToShow.EditRule);
  };

  const handleAddRule = () => {
    setSelectedRule(undefined);
    setModalToShow(ModalToShow.EditRule);
  };

  const closeModal = () => setModalToShow(ModalToShow.None);

  const onRowOrderChange = (params: GridRowOrderChangeParams): void => {
    saveEmailMatch({
      variables: {
        id: params.row.id,
        rule: {
          newIndex: params.targetIndex,
        },
      },
    });
  };

  return (
    <>
      <StyledCard variant="outlined">
        <CardHeader
          title={
            <EmailHeader
              email={email}
              closeDeactivateRecipientModal={() => setModalToShow(ModalToShow.None)}
              openDeactivateRecipientModal={() => setModalToShow(ModalToShow.DeactivateRecipientModal)}
              showDeactivateRecipientModal={modalToShow === ModalToShow.DeactivateRecipientModal}
            />
          }
        />

        <RecipientCardContent>
          <Stack sx={{ alignItems: "flex-end", marginBottom: "8px" }}>
            <Button onClick={handleAddRule} variant="outlined" aria-label="Add rule">
              Add Rule
            </Button>
          </Stack>
          <Box sx={{ maxHeight: "50vh", overflow: "scroll" }}>
            <DataGridPro
              loading={false}
              rows={rules || []}
              columns={columns(deactivateRule, editRule)}
              rowReordering={true}
              hideFooterRowCount
              hideFooter
              onRowOrderChange={onRowOrderChange}
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "16px",
                },
                "& .MuiDataGrid-cell": {
                  fontSize: "14px",
                },
              }}
            />
          </Box>
        </RecipientCardContent>
      </StyledCard>
      <EmailRuleModal
        open={modalToShow === ModalToShow.EditRule}
        recipient={email}
        rule={selectedRule}
        onClose={closeModal}
        updateCache={updateCache}
      />
      <DeactivateRuleWarningModal
        open={modalToShow === ModalToShow.DeactivateRuleWarning}
        ruleId={selectedRule?.id || ""}
        onClose={closeModal}
        onCancel={closeModal}
        updateCache={updateCache}
      />
    </>
  );
};

export { RecipientCard };
